"use client";

import { useCallback } from "react";
import type { HyperliquidAbstractionMode } from "@cede/utils";
import { postHyperliquidInfo } from "@cede/utils";
import { useQuery } from "@tanstack/react-query";

export interface HyperliquidAbstractionModeResult {
  /** Raw abstraction mode string from HL API (null while loading or on error) */
  abstractionMode: HyperliquidAbstractionMode | null;
  /** True for unifiedAccount or portfolioMargin — balance source is spotClearinghouseState only */
  usesUnifiedBalanceSource: boolean;
  /** True for standard or dexAbstraction — user can/needs to transfer between spot and perps */
  canTransferBetweenSpotAndPerps: boolean;
  /** Whether the abstraction mode query is still loading */
  isLoading: boolean;
  /** Error from the abstraction mode query (null if successful or loading) */
  error: Error | null;
}

function isUnifiedMode(abstractionMode: HyperliquidAbstractionMode | null) {
  return abstractionMode === "unifiedAccount" || abstractionMode === "portfolioMargin";
}

/**
 * Read-only hook to query the Hyperliquid account abstraction mode via the HL info API.
 *
 * For `unifiedAccount` and `portfolioMargin` users, all balances are in `spotClearinghouseState`.
 * Individual perp user states are not meaningful.
 *
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/trading/account-abstraction-modes
 */
export function useHyperliquidAbstractionMode(
  userAddress: string | undefined,
  enabled = true,
): HyperliquidAbstractionModeResult {
  const addressKey = userAddress?.toLowerCase();

  const queryKey = ["hyperliquid-account-mode", addressKey] as const;

  const checkAccountMode = useCallback(async (): Promise<HyperliquidAbstractionMode | null> => {
    if (!addressKey) {
      return null;
    }

    return await postHyperliquidInfo<HyperliquidAbstractionMode>({
      type: "userAbstraction",
      user: addressKey,
    });
  }, [addressKey]);

  // Check current abstraction mode via HL info API.
  // Returns: "standard" | "dexAbstraction" | "unifiedAccount" | "portfolioMargin"
  const {
    data: abstractionMode = null,
    isLoading,
    error: queryError,
  } = useQuery<HyperliquidAbstractionMode | null>({
    queryKey,
    queryFn: checkAccountMode,
    enabled: enabled && Boolean(addressKey),
    staleTime: Infinity, // Mode won't change back during a session
    retry: 3,
  });

  const error = queryError as Error | null;
  const usesUnifiedBalanceSource = isUnifiedMode(abstractionMode);
  // Fall back to legacy transfer-capable behavior on query errors rather than hiding transfer UI entirely.
  const canTransferBetweenSpotAndPerps = !isLoading && !usesUnifiedBalanceSource;

  return {
    abstractionMode,
    usesUnifiedBalanceSource,
    canTransferBetweenSpotAndPerps,
    isLoading,
    error,
  };
}
